import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { partnerSubPartnersApi } from '../../api/partner-subpartners-engagements';
import { ApiError } from '../../api/client';
import { DataTable } from '../../components/DataTable';
import { useToast } from '../../components/Toast';

export function PartnerSubPartnersTab({ partnerId }: { partnerId: number }) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const subPartnersQuery = useQuery({ queryKey: ['partner-subpartners', partnerId], queryFn: () => partnerSubPartnersApi.list(partnerId) });
  const { register, handleSubmit, reset } = useForm<{ name: string; subPartnerType: string; relationship: string; agreementReference: string }>();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['partner-subpartners', partnerId] });
  const createMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) => partnerSubPartnersApi.create(partnerId, values),
    onSuccess: () => { showSuccess('Sub-partner added.'); invalidate(); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to add sub-partner.'),
  });
  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) => partnerSubPartnersApi.setDueDiligenceStatus(id, status),
    onSuccess: () => { showSuccess('Status updated.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  return (
    <div className="panel">
      <div className="panel-body">
        {subPartnersQuery.data && (
          <DataTable
            rows={subPartnersQuery.data.data}
            rowKey={(s) => s.id}
            emptyMessage="No sub-partners on file yet."
            columns={[
              { header: 'Name', render: (s) => s.name },
              { header: 'Type', render: (s) => s.sub_partner_type ?? '-' },
              { header: 'Relationship', render: (s) => s.relationship ?? '-' },
              { header: 'Risk', render: (s) => s.risk_rating ?? '-' },
              {
                header: 'DD Status', render: (s) => (
                  <select value={s.due_diligence_status} onChange={(e) => statusMutation.mutate({ id: s.id, status: e.target.value })}>
                    <option value="not_assessed">Not Assessed</option><option value="in_progress">In Progress</option><option value="approved">Approved</option><option value="rejected">Rejected</option>
                  </select>
                ),
              },
            ]}
          />
        )}

        <h3>Add Sub-Partner</h3>
        <form className="inline-form" onSubmit={handleSubmit((values) => createMutation.mutate(values))}>
          <label>Name<input {...register('name', { required: true })} /></label>
          <label>Type<input {...register('subPartnerType')} /></label>
          <label>Relationship<input {...register('relationship')} /></label>
          <label>Agreement Reference<input {...register('agreementReference')} /></label>
          <button type="submit" disabled={createMutation.isPending}>{createMutation.isPending ? 'Adding…' : 'Add'}</button>
        </form>
      </div>
    </div>
  );
}
